/**
 * Game client entrypoint: play a dungeon somebody else built.
 *
 * The flow is now end to end for characters: pick one from the pack, join the battle
 * room carrying that choice as a few strings, and see every other player rendered from
 * theirs. Level loading, input and combat are still to be specified.
 */
import './style.css';
import { createScene } from './engine/createScene.ts';
import { DungeonView } from './world/dungeonView.ts';
import { createSampleLevel } from './world/sampleLevel.ts';
import { BattleConnection } from './net/battleConnection.ts';
import { Hud } from './ui/hud.ts';
import { spawnAvatar, packManifest, type Avatar } from './character/localAvatar.ts';
import { RemoteAvatars } from './character/remoteAvatars.ts';
import { SelectScreen, type SelectionResult } from './character/selectScreen.ts';
import { encodeAppearance, encodeTints, tileToWorld } from '@dungeon-builder/shared';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

const canvas = document.querySelector<HTMLCanvasElement>('#render-canvas');
if (!canvas) throw new Error('#render-canvas is missing from index.html');

const { scene, camera } = createScene(canvas);
const hud = new Hud();

const level = createSampleLevel();
const dungeon = new DungeonView(scene);
dungeon.load(level);

/**
 * Where the level's characters stand.
 *
 * The server integrates movement from its own origin and knows nothing about level
 * geometry yet -- level loading is still to be specified -- so the client treats
 * server coordinates as **relative to the level's spawn point**. Without that a player
 * at the server's (0, 0, 0) stands in the level's corner tile rather than in the room.
 * When the server starts loading levels this becomes its job and `worldFrom` goes away.
 */
const spawnTile = level.tiles.find((tile) => tile.kind === 'spawn');
const spawnCell = spawnTile
  ? tileToWorld(spawnTile.x, spawnTile.z)
  : tileToWorld(Math.floor(level.width / 2), Math.floor(level.height / 2));
const spawn = new Vector3(spawnCell.x, 0.25, spawnCell.z);
const worldFrom = (x: number, y: number, z: number): Vector3 =>
  new Vector3(spawn.x + x, spawn.y + y, spawn.z + z);
const VIEW_RADIUS = 4.5;
camera.beta = Math.PI / 2.6;
camera.lowerRadiusLimit = 1;

/**
 * Points the camera at a character without losing the framing.
 *
 * `ArcRotateCamera.setTarget` keeps the camera *position* and recomputes the radius
 * from it, so re-targeting as the character moves silently pulls the camera away --
 * following a character from the level centre to the origin left it 35 m back, staring
 * at empty floor. The radius has to be restored after every retarget.
 */
let framed: Vector3 | null = null;
function frame(position: Vector3): void {
  if (framed && Vector3.DistanceSquared(framed, position) < 1e-4) return;
  framed = position.clone();
  camera.setTarget(position.add(new Vector3(0, 1, 0)));
  camera.radius = VIEW_RADIUS;
}

frame(spawn);

const connection = new BattleConnection({
  onStatus: (status, detail) => hud.setConnection(status, detail),
  onState: (state) => {
    hud.setPlayers(state.players.size);
    remotes.sync(state);

    // The server owns position -- for this client as much as for its peers. Until
    // input exists the local avatar simply follows its own server record, which also
    // keeps it in the same place as everyone else rather than at the preview spot.
    const own = connection.sessionId ? state.players.get(connection.sessionId) : undefined;
    if (own && local) {
      local.root.position.copyFrom(worldFrom(own.x, own.y, own.z));
      local.root.rotation.y = own.yaw;
      frame(local.root.position);
    }
  },
  onWelcome: (payload) => console.log('[game-client] welcome', payload),
});

const remotes = new RemoteAvatars(scene, () => connection.sessionId, worldFrom);

// --- character selection ------------------------------------------------------

let local: Avatar | null = null;
let previewing = false;

/**
 * Rebuilds the previewed character.
 *
 * Re-entrant on purpose: the select screen fires on every click, and assembling is
 * asynchronous, so a burst of edits must not leave two bodies in the scene.
 */
async function preview(selection: SelectionResult): Promise<void> {
  if (previewing) return;
  previewing = true;
  try {
    local?.dispose();
    local = await spawnAvatar(scene, {
      gender: selection.gender,
      stance: selection.stance,
      position: spawn.clone(),
      colourway: selection.colourway,
      tints: selection.tints,
      variantIds: selection.variantIds,
    });
    framed = null;
    frame(spawn);
  } catch (error) {
    console.error('[character] could not build the character', error);
    hud.setConnection('offline', error instanceof Error ? error.message : String(error));
  } finally {
    previewing = false;
  }
}

const selectHost = document.querySelector<HTMLElement>('#character-select');

void packManifest()
  .then((manifest) => {
    if (!selectHost) return;
    const screen = new SelectScreen(selectHost, {
      manifest,
      onChange: (selection) => void preview(selection),
      onConfirm: (selection) => {
        screen.dispose();
        // The character travels as a few strings; every client already has the pack,
        // so peers render it without any asset transfer.
        void connection.connect({
          levelId: level.id,
          displayName: 'guest',
          character: {
            gender: selection.gender,
            stance: selection.stance,
            packId: manifest.packId,
            appearance: encodeAppearance(selection.variantIds),
            tints: encodeTints(selection.tints),
            colourway: selection.colourway,
          },
        });
      },
    });
  })
  .catch((error: unknown) => {
    console.error('[character] no pack manifest', error);
    if (selectHost) {
      selectHost.innerHTML =
        '<p>No character pack.<br>Run <code>npm run pipeline -- pack starter</code>, then reload.</p>';
    }
  });

window.addEventListener('beforeunload', () => void connection.dispose());

if (import.meta.hot) {
  // Dev-only handle: the scene can be inspected from the console and by the
  // screenshot harness. Stripped from production builds with the HMR block.
  Object.assign(window, {
    dungeon: { scene, camera, local: () => local, remotes, connection, spawnAvatar, spawn },
  });
  import.meta.hot.dispose(() => {
    dungeon.dispose();
    local?.dispose();
    remotes.dispose();
    void connection.dispose();
  });
}
